/**
 * TrialConversionModal (F-201 / T-152-002 + T-151-002, US-152/US-165)
 *
 * Contextual upgrade prompts (AC-5): mounted once in App.tsx, it reads the
 * trial markers trialFlow persisted and renders only inside the conversion
 * window — when 1–4 days remain it shows the upgrade prompt; when the trial
 * has expired it shows the expired prompt with an export escape hatch.
 * Nothing renders when no trial markers exist or the trial has more than
 * 4 days left (no nagging mid-trial).
 *
 * Dismissal is per-session (sessionStorage) so a refresh in the same tab
 * doesn't re-open a modal the visitor just closed; a new tab/session may
 * see it again (deliberate: it is a conversion nudge, not a blocker).
 *
 * Export link follows the market (SignUpForm precedent: getAppMarket()):
 * texas → /texas/compliance-console, else → /gdpr (RET-397 export CTA).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getAppMarket } from '../../services/MarketService';
import { isSuperAdminSession } from '../../utils/buildUserFromToken';
import {
  FIRST_FRUITS_TRIAL_DAYS,
  TIER_DISPLAY_NAMES,
} from '../../services/usdPricingModel';
import { readTrialState } from '../../services/trialFlow';

const DISMISS_KEY = 'trialConversionModalDismissed';
/** Prompt the upgrade when ≤ this many days remain (matches LP day-10 Brevo send for a 14-day trial). */
export const UPGRADE_WINDOW_DAYS = 4;

const tierLabel = (tierId: string): string =>
  TIER_DISPLAY_NAMES[tierId] ||
  tierId.charAt(0).toUpperCase() +
    tierId.slice(1);

const TrialConversionModal: React.FC = () => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const state = readTrialState();
  // Staff accounts are unrestricted — never nag a Super Admin to upgrade a
  // trial/subscription (plan gates do not apply to protected features for them).
  if (!state || dismissed || isSuperAdminSession()) return null;

  const expired = state.daysLeft <= 0;
  const withinWindow = !expired && state.daysLeft <= UPGRADE_WINDOW_DAYS;
  if (!expired && !withinWindow) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const market = getAppMarket();
  const dataHref = market === 'texas' ? '/texas/compliance-console' : '/gdpr';
  const upgradeHref = `/pricing?plan=${state.tier}&upgrade=1`;
  const tierName = tierLabel(state.tier);

  return (
    <div
      className="trial-conversion-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={expired ? 'Trial ended' : 'Trial ending soon'}
      data-testid="trial-conversion-modal"
    >
      <div className="trial-conversion-card" data-testid={expired ? 'trial-expired-modal' : 'trial-upgrade-modal'}>
        {expired ? (
          <>
            <h2>Your {tierName} trial has ended</h2>
            <p>
              Upgrade to keep your {tierName} data, service allowance and dashboards —
              or export a copy of your data first.
            </p>
            <Link
              to={upgradeHref}
              className="subscribe-btn"
              onClick={dismiss}
              data-testid="trial-upgrade-cta"
            >
              Upgrade now
            </Link>
            <Link
              to={dataHref}
              data-testid="trial-export-link"
              onClick={dismiss}
            >
              Export my data
            </Link>
          </>
        ) : (
          <>
            <h2>
              Your {tierName} trial ends in {state.daysLeft}{' '}
              {state.daysLeft === 1 ? 'day' : 'days'}
            </h2>
            <p>
              Upgrade now to keep {tierName} — your data stays either way while you
              decide. The {FIRST_FRUITS_TRIAL_DAYS}-day trial needed no card, and the
              upgrade path is self-serve.
            </p>
            <Link
              to={upgradeHref}
              className="subscribe-btn"
              onClick={dismiss}
              data-testid="trial-upgrade-cta"
            >
              Upgrade now
            </Link>
            <button
              type="button"
              onClick={dismiss}
              data-testid="trial-modal-dismiss"
            >
              Maybe later
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TrialConversionModal;
